import { nanoid } from "nanoid";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  IframeToParentMessage,
  ParentToIframeMessage,
} from "../../../shared/liveBrowserRuntime/protocol";
import { resolveStepColorHex } from "../../lib/stepColor";
import type { ElementSnippet } from "../Workflow";
import type { LiveFeature } from "./feature";

type Opts = {
  enabled?: boolean;
  stepColor?: string;
  onElementSelect?: (
    element: Omit<ElementSnippet, "stepName" | "color">
  ) => void;
  onElementDeselect?: (index: number) => void;
  elementSnippets?: readonly ElementSnippet[];
};

export type InspectMode = {
  feature: LiveFeature;
};

export default function useInspectMode({
  enabled,
  stepColor,
  onElementSelect,
  onElementDeselect,
  elementSnippets,
}: Opts): InspectMode {
  const [hex, setHex] = useState(() => resolveStepColorHex(stepColor ?? "sky"));
  useEffect(() => {
    const resolve = () => setHex(resolveStepColorHex(stepColor ?? "sky"));
    resolve();
    const root = document.documentElement;
    const obs = new MutationObserver(resolve);
    obs.observe(root, { attributes: true, attributeFilter: ["style"] });
    return () => obs.disconnect();
  }, [stepColor]);

  const onSelectRef = useRef(onElementSelect);
  onSelectRef.current = onElementSelect;
  const onDeselectRef = useRef(onElementDeselect);
  onDeselectRef.current = onElementDeselect;
  const snippetsRef = useRef(elementSnippets);
  snippetsRef.current = elementSnippets;
  const hexRef = useRef(hex);
  hexRef.current = hex;

  const portRef = useRef<MessagePort | null>(null);

  useEffect(() => {
    portRef.current?.postMessage({ type: "3b-color", hex });
  }, [hex]);

  useEffect(() => {
    portRef.current?.postMessage({
      type: "3b-set-enabled",
      enabled: !!enabled,
    });
  }, [enabled]);

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const feature = useMemo<LiveFeature>(
    () => ({
      attach: (api) => {
        portRef.current = api.port;
        const port = api.port;
        if (!port) return;

        const send = (message: ParentToIframeMessage) => {
          try {
            port.postMessage(message);
          } catch {}
        };

        const restoreSnippets = () => {
          const snippets = snippetsRef.current;
          if (!snippets || snippets.length === 0) return;
          send({
            type: "3b-restore",
            items: snippets.map((s) => ({
              index: s.index,
              descriptor: s.descriptor,
              cssPath: s.cssPath,
              tagName: s.tagName,
            })),
          });
        };

        send({ type: "3b-color", hex: hexRef.current });
        send({ type: "3b-set-enabled", enabled: !!enabledRef.current });
        restoreSnippets();

        const onMessage = (event: MessageEvent) => {
          const data = event.data as IframeToParentMessage | undefined;
          if (!data) return;
          if (data.type === "3b-element-select") {
            onSelectRef.current?.({
              id: nanoid(),
              tagName: data.tagName,
              descriptor: data.descriptor,
              label: data.label,
              cssPath: data.cssPath,
              outerHtml: data.outerHtml,
              textContent: data.textContent,
              index: data.index,
            });
            return;
          }
          if (data.type === "3b-element-group-select") {
            onSelectRef.current?.({
              id: nanoid(),
              tagName: "group",
              descriptor: data.descriptor,
              outerHtml: data.outerHtml,
              textContent: data.textContent,
              index: data.index,
            });
            return;
          }
          if (data.type === "3b-element-deselect") {
            onDeselectRef.current?.(data.index);
            return;
          }
          if (data.type === "3b-ready") {
            restoreSnippets();
          }
        };
        port.addEventListener("message", onMessage);
        port.start();

        // Snippet list and other UI dispatch these on window; relay in.
        const onHighlight = (event: Event) => {
          const index = (event as CustomEvent).detail?.index;
          send({ type: "3b-highlight", index });
        };
        const onDeselect = (event: Event) => {
          const index = (event as CustomEvent).detail?.index;
          if (index != null) send({ type: "3b-deselect", index });
        };
        window.addEventListener("3b-highlight-element", onHighlight);
        window.addEventListener("3b-deselect-element", onDeselect);
        return () => {
          port.removeEventListener("message", onMessage);
          window.removeEventListener("3b-highlight-element", onHighlight);
          window.removeEventListener("3b-deselect-element", onDeselect);
        };
      },
    }),
    []
  );

  return { feature };
}
